import React, { useState } from "react";
import styled from "styled-components";
import { FaGithub, FaLinkedin, FaTwitter, FaGlobe } from "react-icons/fa";

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  padding: 2rem;
  background: linear-gradient(135deg, #1f1f1f, #111);
  overflow-y: auto;
  border-right: 2px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 1024px) {
    border-right: none;
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  }
`;

const RightPanel = styled.div`
  flex: 1;
  padding: 2rem;
  background: linear-gradient(135deg, #0d0d0d, #1a1a1a);
  overflow-y: auto;
  position: relative;

  @media (max-width: 1024px) {
    padding: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FieldWrapper = styled.div`
  margin-bottom: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  border: none;
  outline: none;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  margin-bottom: 0.5rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  border: none;
  outline: none;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  margin-bottom: 0.5rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  border: none;
  outline: none;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  margin-bottom: 0.5rem;
`;

const Button = styled.button`
  padding: 1rem 1.5rem;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  color: #fff;
  cursor: pointer;
  margin-top: 1rem;
  width: 100%;
  font-weight: bold;
`;

const PreviewCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  padding: 2rem;
  color: #fff;
  box-shadow: 0 10px 30px rgba(255, 122, 24, 0.2);
  transition: transform 0.3s ease;
  margin-bottom: 2rem;

  &:hover {
    transform: scale(1.02);
  }
`;

const SkillChip = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  padding: 0.4rem 0.8rem;
  margin: 0.2rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: bold;
`;

export default function PortfolioBuilder() {
  const roles = [
    "Frontend",
    "Backend",
    "Fullstack",
    "Mobile",
    "Data Scientist",
    "Other",
  ];
  const experiences = Array.from({ length: 31 }, (_, i) =>
    i === 0 ? "Beginner" : `${i} yrs`
  );
  const jobModes = ["Remote", "Onsite", "Hybrid"];

  const [form, setForm] = useState({
    photo: null,
    name: "",
    email: "",
    mobile: "",
    location: "",
    bio: "",
    role: "",
    customRole: "",
    company: "",
    salary: "",
    jobMode: "",
    experience: "",
    skills: "",
    github: "",
    linkedin: "",
    twitter: "",
    website: "",
    projects: [{ name: "", url: "" }],
  });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
  const handlePhoto = (e) =>
    setForm({ ...form, photo: URL.createObjectURL(e.target.files[0]) });
  const handleProjectChange = (index, e) => {
    const projects = [...form.projects];
    projects[index][e.target.name] = e.target.value;
    setForm({ ...form, projects });
  };
  const addProject = () =>
    setForm({ ...form, projects: [...form.projects, { name: "", url: "" }] });
  const removeProject = (index) =>
    setForm({ ...form, projects: form.projects.filter((_, i) => i !== index) });

  return (
    <Container>
      {/* Left Input Panel */}
      <LeftPanel>
        <SectionTitle>Portfolio Builder</SectionTitle>
        <FieldWrapper>
          <label>Photo</label>
          <Input type="file" accept="image/*" onChange={handlePhoto} />
          <Input
            name="name"
            placeholder="Full Name"
            value={form.name}
            onChange={handleChange}
          />
          <Input
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
          />
          <Input
            name="mobile"
            placeholder="Mobile"
            value={form.mobile}
            onChange={handleChange}
          />
          <Input
            name="location"
            placeholder="Location"
            value={form.location}
            onChange={handleChange}
          />
          <TextArea
            name="bio"
            placeholder="Bio"
            value={form.bio}
            onChange={handleChange}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Select name="role" value={form.role} onChange={handleChange}>
            <option value="">Select Role</option>
            {roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
          {form.role === "Other" && (
            <Input
              name="customRole"
              placeholder="Enter Role"
              value={form.customRole}
              onChange={handleChange}
            />
          )}
          <Select
            name="experience"
            value={form.experience}
            onChange={handleChange}
          >
            <option value="">Select Experience</option>
            {experiences.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </Select>
          <Input
            name="company"
            placeholder="Current Company"
            value={form.company}
            onChange={handleChange}
          />
          <Input
            name="salary"
            placeholder="Current Salary"
            type="number"
            value={form.salary}
            onChange={handleChange}
          />
          <Select name="jobMode" value={form.jobMode} onChange={handleChange}>
            <option value="">Select Job Mode</option>
            {jobModes.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </Select>
        </FieldWrapper>
        <FieldWrapper>
          <TextArea
            name="skills"
            placeholder="Skills (comma separated)"
            value={form.skills}
            onChange={handleChange}
          />
        </FieldWrapper>
        <SectionTitle>Projects</SectionTitle>
        {form.projects.map((p, i) => (
          <FieldWrapper key={i}>
            <Input
              name="name"
              placeholder="Project Name"
              value={p.name}
              onChange={(e) => handleProjectChange(i, e)}
            />
            <Input
              name="url"
              placeholder="Project URL"
              value={p.url}
              onChange={(e) => handleProjectChange(i, e)}
            />
            {form.projects.length > 1 && (
              <Button type="button" onClick={() => removeProject(i)}>
                Remove Project
              </Button>
            )}
          </FieldWrapper>
        ))}
        <Button type="button" onClick={addProject}>
          Add Project
        </Button>
      </LeftPanel>

      {/* Right Preview Panel */}
      <RightPanel>
        <PreviewCard>
          {form.photo && (
            <img
              src={form.photo}
              alt="Profile"
              style={{ width: 100, borderRadius: "50%", marginBottom: 10 }}
            />
          )}
          <h2>{form.name || "Your Name"}</h2>
          <p>{form.bio || "Short bio or about me..."}</p>
          <p>
            <strong>Role:</strong>{" "}
            {form.role === "Other" ? form.customRole : form.role} |{" "}
            <strong>Experience:</strong> {form.experience}
          </p>
          <p>
            <strong>Company:</strong> {form.company} | <strong>Salary:</strong>{" "}
            {form.salary} | <strong>Job Mode:</strong> {form.jobMode}
          </p>
          <h3>Skills:</h3>
          {form.skills
            .split(",")
            .filter((s) => s)
            .map((s, i) => (
              <SkillChip key={i}>{s.trim()}</SkillChip>
            ))}
          <h3>Projects:</h3>
          <ul>
            {form.projects.map((p, i) => (
              <li key={i}>
                <strong>{p.name}</strong>:{" "}
                <a href={p.url} target="_blank" style={{ color: "#ff7a18" }}>
                  {p.url}
                </a>
              </li>
            ))}
          </ul>
        </PreviewCard>
      </RightPanel>
    </Container>
  );
}
